import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { useEffect } from 'react';

const FormSchema = z.object({
  pin: z.string().min(6, {
    message: 'Your one-time password must be 6 characters.',
  }),
});

type InputOTPFormProps = {
  onSubmit: (data: z.infer<typeof FormSchema>) => void;
  description?: string;
  errorMessage?: string;
  disabled?: boolean;
  isLoading?: boolean;
  resetForm?: boolean;
};

export function InputOTPForm({
  onSubmit,
  description,
  errorMessage,
  disabled,
  isLoading,
  resetForm,
}: InputOTPFormProps) {
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      pin: '',
    },
  });

  useEffect(() => {
    if (resetForm) {
      form.reset();
    }
  }, [resetForm]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="mx-auto w-full space-y-6"
      >
        <FormField
          control={form.control}
          name="pin"
          render={({ field }) => (
            <FormItem className="flex flex-col items-center">
              <FormDescription className="pb-1 text-center">
                {description}
              </FormDescription>
              <FormControl>
                <InputOTP
                  maxLength={6}
                  {...field}
                  className="flex justify-center "
                  disabled={disabled}
                >
                  <InputOTPGroup className="text-gray-800">
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormMessage className="text-red-500">{errorMessage}</FormMessage>
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? 'Verifying...' : 'Submit'}
        </Button>
      </form>
    </Form>
  );
}
